/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable react/no-danger */
import { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { ThemeGlobal } from '@styles/theme-global';
import { convertScriptChats, isServer } from '@utils/helper';
import { useGoogleAnalytics } from '@utils/hooks/useGoogleAnalytics';
import { useLeadFormCapture } from '@containers/Home/hook/useLeadFormCapture';
import { HomeWrapper } from './styled';
import {
  selectSceneSelected,
  selectThemeConfigGlobal,
  selectTitleProject,
  selectPopupConfigGlobal,
  selectConfigProtectPassword,
  selectDetailProject,
  selectScriptChats,
  selectListCallToAction,
  selectCallToActionConfigGlobal,
} from './selectors';
import { useCheckPasswordTour } from './hook/useCheckPasswordTour';
import useGeneralFunction from './hook/useGeneralFunction';
import { GET_LIST_HOTSPOT_FLYCAM, UPDATE_COUNT_VIEW_SCENE } from './slice';
import { selectProvinceSelected } from '@containers/App/selectors';
import { RADIUS } from './constants';

const ThemeGoterest = dynamic(
  () => import('@containers/Home/components/Theme/ThemeGoterest'),
);

const ProtectPassword = dynamic(() => import('@components/ProtectPassword'));
const PopupIntro = dynamic(
  () => import('@containers/Home/components/PopupIntro'),
  { ssr: false },
);
const ActionHotspot = dynamic(() => import('./components/ActionHotspot'), {
  ssr: false,
});
const Collaboration = dynamic(() => import('./components/Collaboration'), {
  ssr: false,
});
const CommentGoterest = dynamic(() => import('./components/CommentGoterest'), {
  ssr: false,
});
const DialogFormCapture = dynamic(
  () => import('@components/DialogFormCapture'),
  { ssr: false },
);
const CallToActions = dynamic(() => import('@components/CallToActions'));
const DangerouslySetHtmlContent = dynamic(
  () => import('@components/DangerouslySetHtmlContent'),
);

interface Props {
  isPublic?: boolean;
  isExport?: boolean;
  isCollaborationMode?: boolean;
  // NOTE: is case custom domain => not save scene on url
  isPreventChangeUrl?: boolean;
  // NOTE: fetch cookies from server
  passwordTourCookie?: string;
  listProjectGuardOpenedCookie?: string;
  listFormCaptureOpenedCookie?: string;
}

function Home({
  isPublic,
  isExport,
  isCollaborationMode = false,
  isPreventChangeUrl = false,
  passwordTourCookie,
  listProjectGuardOpenedCookie,
  listFormCaptureOpenedCookie,
}: Props) {
  const router = useRouter();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null!);

  const scriptChats = useSelector(selectScriptChats);
  const titleProject = useSelector(selectTitleProject);
  const detailProject = useSelector(selectDetailProject);
  const sceneSelected = useSelector(selectSceneSelected);
  const popupConfig = useSelector(selectPopupConfigGlobal);
  const themeConfig = useSelector(selectThemeConfigGlobal);
  const listCallToAction = useSelector(selectListCallToAction);
  const provinceSelected = useSelector(selectProvinceSelected);
  const callToActionConfigGlobal = useSelector(selectCallToActionConfigGlobal);

  const configProtectPassword = useSelector(selectConfigProtectPassword);

  const { onUpdatePageViewByUrl } = useGoogleAnalytics();
  const { nextScene, onNextScene } = useGeneralFunction();

  const { isOpenGuardPassword, onCheckGuardPassword } = useCheckPasswordTour({
    detailProject,
    configProtectPassword,
    passwordTourCookie,
    listProjectGuardOpenedCookie,
  });

  const {
    onCloseFormCapture,
    formCaptureSelected,
    isVisibleFormCapture,
    formCaptureConfigGlobal,
    formCaptureConfigSelected,
    onCheckShowFormCaptureOnScene,
    onCheckShowFormCaptureOnProject,
  } = useLeadFormCapture({
    listFormCaptureOpenedCookie,
  });

  const scriptsChatConverted = useMemo(
    () =>
      scriptChats && scriptChats?.length
        ? convertScriptChats(scriptChats)
        : undefined,
    [scriptChats],
  );

  useEffect(() => {
    if (sceneSelected && !isPreventChangeUrl && provinceSelected) {
      if (!isCollaborationMode) {
        if (isExport)
          window.history.pushState(
            null,
            null,
            `${process.env.NEXT_PUBLIC_SUBFOLDER || ''}/?scene=${
              sceneSelected.id
            }`,
          );
        else {
          const url = isPublic
            ? `${process.env.NEXT_PUBLIC_BASE_URL_PUBLIC}/${router.query.province}/discover/${router.query.tour}/${sceneSelected.id}`
            : `${process.env.NEXT_PUBLIC_BASE_URL_PREVIEW}?province=${router.query.province}&tour=${router.query.tour}&scene=${sceneSelected.id}`;
          router.push(url, undefined, { shallow: true });
        }
      } else {
        const url = `${process.env.NEXT_PUBLIC_BASE_URL_COLLABORATION}?province=${router.query.province}&tour=${router.query.tour}&scene=${sceneSelected.id}`;
        if (!isServer) window.history.pushState(null, null, url);
      }
    }
    if (sceneSelected) {
      onUpdatePageViewByUrl();
      dispatch(UPDATE_COUNT_VIEW_SCENE({ id: sceneSelected?.id }));
      if (detailProject?.type === 'flycam')
        dispatch(
          GET_LIST_HOTSPOT_FLYCAM({
            province: provinceSelected,
            lat: sceneSelected?.lat || detailProject?.lat,
            lng: sceneSelected?.lng || detailProject?.lng,
            km: RADIUS,
          }),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneSelected?.id, detailProject?.type]);

  // CHECK FORM CAPTURE TO SHOW
  useEffect(() => {
    if (isVisibleFormCapture === null && detailProject?.id)
      onCheckShowFormCaptureOnProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailProject?.id]);

  useEffect(() => {
    if (isVisibleFormCapture === false && sceneSelected)
      onCheckShowFormCaptureOnScene(sceneSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisibleFormCapture, sceneSelected]);

  return isOpenGuardPassword ? (
    <ProtectPassword
      scene={sceneSelected}
      password={configProtectPassword?.password}
      onSubmit={onCheckGuardPassword}
    />
  ) : (
    <>
      <HomeWrapper ref={containerRef}>
        {isCollaborationMode && <Collaboration />}
        <CommentGoterest />
        <div className="mainContent">
          {!!titleProject && (
            <ThemeGoterest
              onChangeScene={onNextScene}
              onNextScene={onNextScene.bind(this, nextScene)}
            />
          )}
          <ActionHotspot />
          {!!listCallToAction.length && (
            <CallToActions
              listCallToAction={listCallToAction}
              callToActionConfig={callToActionConfigGlobal}
            />
          )}
          <PopupIntro />

          {!!formCaptureSelected?.id && (
            <DialogFormCapture
              onCancel={onCloseFormCapture}
              visible={isVisibleFormCapture}
              formCaptureSelected={formCaptureSelected}
              formCaptureConfigGlobal={formCaptureConfigGlobal}
              formCaptureConfigSelected={formCaptureConfigSelected}
            />
          )}
        </div>
        <ThemeGlobal
          selected={themeConfig?.selected || 'default'}
          primaryColor={themeConfig?.config?.primaryColor}
          secondaryColor={
            themeConfig?.selected === 'custom1'
              ? themeConfig?.config?.secondaryColor
              : themeConfig?.config?.primaryColor
          }
          textColor={themeConfig?.config?.textColor}
          popupBgColor={popupConfig?.bgColor}
          popupTextColor={popupConfig?.textColor}
        />
      </HomeWrapper>

      {/* NOTE: Load scriptChats */}
      {!!scriptsChatConverted?.htmls && (
        <DangerouslySetHtmlContent html={scriptsChatConverted?.htmls} />
      )}
      {!!scriptsChatConverted?.scripts && (
        <DangerouslySetHtmlContent html={scriptsChatConverted?.scripts} />
      )}
    </>
  );
}

export default memo(Home);
